import React, {Fragment} from "react";
import {GameLevel, GameLevelStatus} from "data/enums";
import styled from "@emotion/styled";
import {ReactComponent as MlbHitter} from "assets/img/icons/mlb_hitter.svg";
import {ReactComponent as StarWhite} from "assets/img/icons/star_white.svg";
import {ReactComponent as Trophy} from "assets/img/icons/trophy.svg";
import {Exist} from "views/components/exist/exist.component";
import {ReactComponent as UpLevelIcon} from "assets/img/icons/up_level.svg";
import {ReactComponent as DownLevelIcon} from "assets/img/icons/down_level.svg";

const Wrapper = styled.div<{scale: number}>`
	position: relative;
	width: calc(46px * ${(props) => props.scale});
	height: calc(46px * ${(props) => props.scale});
`;

const OuterBorder = styled.div<{scale: number}>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 46px;
	height: 46px;
	padding: 3px;
	border-radius: 50%;
	transform-origin: left top;
	transform: scale(${(props) => props.scale});

	&.${GameLevel.HIGH_A}, &.${GameLevel.SINGLE_A} {
		--inner-border: #99fe88;
		--inner-background: #64f751;
		--outer-border: rgba(153, 254, 136, 0.5);
		--half-circle-color: #34cf3d;
		--hitter-color: #009426;
	}

	&.${GameLevel.AA}, &.${GameLevel.AAA} {
		--inner-border: #48b1ff;
		--inner-background: #008dff;
		--outer-border: rgba(72, 177, 255, 0.5);
		--half-circle-color: #006cff;
		--hitter-color: #1731cc;
	}

	&.${GameLevel.MLB_STARTER}, &.${GameLevel.MLB_CALL_UP} {
		--inner-border: #ffdf7b;
		--inner-background: #ffc803;
		--outer-border: rgba(255, 223, 123, 0.5);
		--half-circle-color: #f89600;
		--hitter-color: #ff8a00;
	}

	&.${GameLevel.MLB_ALL_STAR}, &.${GameLevel.MLB_HALL_OF_FAMER} {
		--inner-border: #ffb54c;
		--inner-background: #ff9500;
		--outer-border: rgba(255, 181, 76, 0.5);
		--half-circle-color: #f57900;
		--hitter-color: #e54d02;
	}

	&.disabled {
		--inner-border: #d9dee4;
		--inner-background: #848990;
		--outer-border: rgba(255, 181, 76, 0);
		--half-circle-color: #62676e;
		--hitter-color: #3d434b;
	}

	border: 2px solid var(--outer-border, rgba(72, 177, 255, 0.5));
`;
const Circle = styled.div`
	width: 40px;
	min-width: 40px;
	flex: 0 0 100%;
	height: 40px;
	border-radius: 40px;
	border: 3px solid var(--inner-border, #48b1ff);
	background: var(--inner-background, #008dff);
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	&:before {
		position: absolute;
		bottom: -3px;
		content: "";
		border-radius: 50%;
		display: block;
		left: -3px;
		width: calc(100% + 6px);
		height: calc(100% + 6px);
		color: var(--half-circle-color, #006cff);
		background: linear-gradient(
			45deg,
			currentColor,
			currentColor 50%,
			transparent 50%,
			transparent
		);
		opacity: 0.3;
	}
`;

const HitterImage = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	border: 3px;
	border-radius: 50%;
	overflow: hidden;
	svg {
		width: 35px;
		transform: translateX(3px);
		color: var(--hitter-color, #006cff);
	}
`;

const Icons = styled.div`
	position: absolute;
	bottom: 3px;
	left: 0;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 1px;
`;

const Status = styled.div`
	position: absolute;
	bottom: 3px;
	right: 0;
	z-index: 3;
	transform: translate(10%, 10%);
	width: 37%;
	height: 37%;
	svg {
		width: 100%;
		height: auto;
	}
`;

interface IProps {
	level?: GameLevel | null;
	disabled?: boolean;
	status?: GameLevelStatus | null;
	size?: number;
}

export const LevelBadge: React.FC<IProps> = ({
	level = GameLevel.SINGLE_A,
	disabled,
	size = 46,
	status,
}) => {
	const scale = size / 46;
	const gameLevel = level || GameLevel.SINGLE_A;

	return (
		<Wrapper scale={scale}>
			<OuterBorder className={`${gameLevel} ${disabled ? "disabled" : ""}`} scale={scale}>
				<Circle>
					<HitterImage>
						<MlbHitter />
					</HitterImage>
					<Icons>
						{
							{
								[GameLevel.SINGLE_A]: (
									<Fragment>
										<StarWhite />
									</Fragment>
								),
								[GameLevel.HIGH_A]: (
									<Fragment>
										<StarWhite />
										<StarWhite />
									</Fragment>
								),

								[GameLevel.AA]: (
									<Fragment>
										<StarWhite />
									</Fragment>
								),
								[GameLevel.AAA]: (
									<Fragment>
										<StarWhite />
										<StarWhite />
									</Fragment>
								),

								[GameLevel.MLB_CALL_UP]: (
									<Fragment>
										<StarWhite />
									</Fragment>
								),
								[GameLevel.MLB_STARTER]: (
									<Fragment>
										<StarWhite />
										<StarWhite />
									</Fragment>
								),

								[GameLevel.MLB_ALL_STAR]: (
									<Fragment>
										<StarWhite />
										<StarWhite />
										<StarWhite />
									</Fragment>
								),
								[GameLevel.MLB_HALL_OF_FAMER]: (
									<Fragment>
										<Trophy />
									</Fragment>
								),
							}[gameLevel]
						}
					</Icons>
				</Circle>
			</OuterBorder>
			<Exist when={status}>
				<Status>
					<Exist when={status === GameLevelStatus.Demoted}>
						<DownLevelIcon />
					</Exist>
					<Exist when={status === GameLevelStatus.Promoted}>
						<UpLevelIcon />
					</Exist>
				</Status>
			</Exist>
		</Wrapper>
	);
};
